import { Injectable } from '@angular/core';
import { ProductVariant } from '../interfaces/wheels';

export interface PricingCalculation {
  frontPrice: number;
  rearPrice: number;
  totalPrice: number;
}

export interface PriceBreakdown {
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  salePrice?: number;
  finalPrice: number;
}

@Injectable({
  providedIn: 'root'
})
export class WheelPricingService {

  /**
   * Calculates pricing for staggered wheel setup (search-by-size)
   */
  calculateStaggeredPrice(
    product: ProductVariant,
    frontQty: number,
    rearQty: number,
    frontSalePrice?: number,
    rearSalePrice?: number
  ): PricingCalculation {
    
    const frontUnitPrice = product?.uae_retail_price || product?.price || 0;
    const rearUnitPrice = product?.rear_uae_retail_price || product?.rear_price || frontUnitPrice;
    
    // Calculate front wheel pricing
    let frontPrice = frontUnitPrice * frontQty;
    if (frontSalePrice && frontSalePrice > 0) {
      frontPrice = frontSalePrice * frontQty;
    }
    
    // Calculate rear wheel pricing
    let rearPrice = rearUnitPrice * rearQty;
    if (rearSalePrice && rearSalePrice > 0) {
      rearPrice = rearSalePrice * rearQty;
    }
    
    const totalPrice = frontPrice + rearPrice;
    
    return { frontPrice, rearPrice, totalPrice };
  }

  /**
   * Calculates pricing for regular wheel setup (vehicle-based search)
   */
  calculateRegularPrice(
    product: ProductVariant, 
    quantity: number,
    salePrice?: number
  ): number {
    const unitPrice = product?.uae_retail_price || product?.price || 0;
    
    if (salePrice && salePrice > 0) {
      return salePrice * quantity;
    }
    
    return unitPrice * quantity;
  }

  /**
   * Gets price breakdown for a single wheel type
   */
  getPriceBreakdown(
    unitPrice: number,
    quantity: number,
    salePrice?: number
  ): PriceBreakdown {
    const totalPrice = unitPrice * quantity;
    const finalPrice = salePrice ? (salePrice * quantity) : totalPrice;
    
    return {
      unitPrice,
      quantity,
      totalPrice,
      salePrice,
      finalPrice
    };
  }

  /**
   * Formats price for display
   */
  formatPrice(price: number, currency: string = '$'): string {
    return `${currency}${price.toFixed(2)}`;
  }

  /**
   * Calculates set pricing (typically set of 4 wheels)
   */
  calculateSetPrice(unitPrice: number, setSize: number = 4): number {
    return unitPrice * setSize;
  }

  /**
   * Determines if staggered pricing should be used
   */
  shouldUseStaggeredPricing(product: ProductVariant, isSearchBySize: boolean): boolean {
    return isSearchBySize && 
           product?.rear_size != null && 
           (product?.rear_uae_retail_price != null || product?.rear_price != null);
  }

  /**
   * Gets display price for product listing
   */
  getDisplayPrice(
    product: ProductVariant, 
    isSearchBySize: boolean, 
    isRear: boolean = false
  ): string {
    if (this.shouldUseStaggeredPricing(product, isSearchBySize)) {
      const frontPrice = (product.uae_retail_price || product.price || 0) * 2;
      const rearPrice = (product.rear_uae_retail_price || product.rear_price || product.uae_retail_price || product.price || 0) * 2;
      const totalPrice = frontPrice + rearPrice;
      return this.formatPrice(totalPrice) + ' / set of 4';
    } else {
      const setPrice = this.calculateSetPrice(product.uae_retail_price || product.price || 0);
      return this.formatPrice(setPrice) + ' / set of 4';
    }
  }
}
